import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/blacksmith-ui/button";
import { GitHubIcon } from "@/components/GitHubIcon";
import { githubReconnectLogin } from "@/lib/github-reauth";
import { LogIn, RefreshCw, TriangleAlert } from "lucide-react";
interface GithubSsoReauthorizationErrorProps {
  onRetry?: () => void;
  /**
   * Runs the silent OAuth re-login that re-authorizes the token for orgs the
   * user holds active SAML sessions for. Defaults to the production flow;
   * overridable for stories and tests.
   */
  onReconnect?: () => void;
}

/**
 * Full-view error screen shown when GitHub hides every org the user has from
 * Blacksmith because their OAuth token lost its SAML SSO authorization
 * (commonly after the org changes its SSO configuration). Walks the user
 * through re-authorizing on GitHub and reconnecting, instead of the generic
 * "something went wrong" screen.
 *
 * Styled as a pixel terminal: an `SSO` display face, a Blacksmith-yellow accent
 * rule, and a monospace `auth: saml` status line. Renders inside app chrome, so
 * it keeps the existing `min-h-[60vh]` height.
 */
export function GithubSsoReauthorizationError(t0) {
  const $ = _c(16);
  const {
    onRetry,
    onReconnect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(var(--pattern_color)_0.5px,_transparent_0)] bg-[size:16px_16px] opacity-60 [--pattern_color:var(--color-black)]/15 dark:[--pattern_color:var(--color-white)]/7" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <GitHubIcon className="h-full w-full text-neutral-900 dark:text-neutral-100" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="relative mb-4 h-10 w-10">{t2}<div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white ring-2 ring-[var(--color-app-background)]"><TriangleAlert className="h-3 w-3" /></div></div>;
    t4 = <div className="type-numerals-pixel-l text-neutral-900 dark:text-neutral-100">SSO</div>;
    t5 = <div className="mt-2 h-1 w-16 rounded-full bg-blacksmith-yellow" />;
    t6 = <h1 className="mt-8 type-page-title-m text-neutral-900 dark:text-neutral-100">GitHub single sign-on authorization needed</h1>;
    t7 = <p className="mt-2 type-body-s-regular text-muted-foreground">Your SAML single sign-on session expired, so GitHub is hiding your organization. Re-authorize on GitHub, then reconnect to refresh your access.</p>;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="mt-2 type-body-s-regular text-muted-foreground">Open your organization on{" "}<a href="https://github.com" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">github.com</a>{" "}and complete the single sign-on prompt, then reconnect.</p>;
    t9 = <p className="mt-3 type-code-block text-muted-foreground">auth: saml · reauthorize_required</p>;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  let t10;
  if ($[9] !== onReconnect) {
    t10 = <Button icon={LogIn} onClick={() => {
      if (onReconnect) {
        onReconnect();
      } else {
        githubReconnectLogin();
      }
    }}>Reconnect GitHub</Button>;
    $[9] = onReconnect;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== onRetry) {
    t11 = <Button variant="secondary" icon={RefreshCw} onClick={() => {
      if (onRetry) {
        onRetry();
      } else {
        window.location.reload();
      }
    }}>Try again</Button>;
    $[11] = onRetry;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10 || $[14] !== t11) {
    t12 = <div className="relative flex min-h-[60vh] items-center justify-center overflow-hidden bg-app-background p-6">{t1}<div className="relative flex max-w-md flex-col items-center text-center">{t3}{t4}{t5}{t6}{t7}{t8}{t9}<div className="mt-6 flex items-center justify-center gap-3">{t10}{t11}</div></div></div>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  return t12;
}